/**
 * Withdrawals & Payouts Articles
 */

import type { HelpArticle } from "../types";

export const WITHDRAWALS_ARTICLES: HelpArticle[] = [
  {
    id: "wd-001",
    slug: "payout-process-overview",
    title: "Payout Process Overview",
    description: "How withdrawals to your bank account work",
    category: "withdrawals",
    popular: true,
    order: 1,
    keywords: ["payout", "withdrawal", "bank", "process", "transfer"],
    content: `
# Payout Process Overview

After your crypto is converted, here's how you receive fiat in your bank account.

## The Payout Flow

1. **Conversion Complete** - Crypto converted to fiat
2. **Balance Available** - Funds appear in your account
3. **Payout Initiated** - Automatic or manual
4. **Bank Transfer** - Sent via your configured method
5. **Funds Arrive** - Money in your bank account

## Automatic vs Manual Payouts

### Automatic Payouts
- Funds sent immediately after conversion
- No action required
- Configure threshold in settings
- Best for regular users

### Manual Payouts
- Request withdrawal when ready
- Control timing
- Consolidate multiple deposits
- Good for larger amounts

## Payout Methods

| Method | Regions | Speed |
|--------|---------|-------|
| ACH | USA | 2-3 business days |
| Wire | USA, International | 1-2 business days |
| SEPA | Europe | 1 business day |
| SPEI | Mexico | Same day |

## Setting Up Payouts

1. Complete KYC verification
2. Add bank account details
3. Verify bank ownership
4. Configure payout preferences
5. Start receiving funds

## Payout Limits

| Tier | Daily Limit | Monthly Limit |
|------|-------------|---------------|
| Standard | $25,000 | $100,000 |
| Enhanced | $100,000 | $500,000 |
| Enterprise | Custom | Custom |
    `.trim(),
  },
  {
    id: "wd-002",
    slug: "adding-bank-accounts",
    title: "Adding Bank Accounts",
    description: "How to connect your bank account for payouts",
    category: "withdrawals",
    popular: true,
    order: 2,
    keywords: ["bank", "account", "add", "connect", "setup"],
    content: `
# Adding Bank Accounts

Connect your bank account to receive fiat payouts.

## Requirements

- Completed KYC verification
- Bank account in your name
- Account supports incoming transfers

## Adding a Bank Account

### Step 1: Navigate to Settings
1. Go to Settings > Payouts
2. Click "Add Bank Account"
3. Select your country

### Step 2: Enter Details

**For US Accounts (ACH)**
- Routing number (9 digits)
- Account number
- Account type (Checking/Savings)
- Account holder name

**For US Accounts (Wire)**
- Same as ACH plus
- Bank name
- Bank address

**For European Accounts (SEPA)**
- IBAN
- BIC/SWIFT (optional)
- Account holder name

**For Mexican Accounts (SPEI)**
- CLABE number (18 digits)
- Bank name
- Account holder name

### Step 3: Verify Account
- Micro-deposit verification (ACH)
- Or document verification
- Typically 1-2 business days

## Account Verification

### Micro-Deposits (ACH)
1. We send two small deposits
2. Check your bank statement
3. Enter amounts in Supabank
4. Account verified!

### Document Verification
1. Upload bank statement
2. Must show your name
3. Must show account number
4. Verified within 24 hours

## Multiple Accounts

- Add up to 5 bank accounts
- Set a default for payouts
- Switch accounts anytime
- Remove accounts as needed

## Tips

**Do:**
- Use accounts in your legal name
- Double-check all numbers
- Use accounts you regularly access

**Don't:**
- Use third-party accounts
    `.trim(),
  },
  {
    id: "wd-003",
    slug: "supported-payout-currencies",
    title: "Supported Payout Currencies",
    description: "Available currencies for bank payouts",
    category: "withdrawals",
    order: 3,
    keywords: ["currency", "fiat", "usd", "eur", "mxn"],
    content: `
# Supported Payout Currencies

Receive payouts in your preferred fiat currency.

## Available Currencies

| Currency | Symbol | Regions |
|----------|--------|---------|
| US Dollar | USD | USA, International |
| Euro | EUR | Europe, International |
| Mexican Peso | MXN | Mexico |

## Currency Selection

### During Conversion
- Crypto converts to selected fiat
- Set default in preferences
- Change per-transaction (coming soon)

### Payout Currency
- Must match bank account currency
- Or currency conversion applied

## Exchange Rates

- Real-time market rates
- Competitive spreads
- Transparent pricing
- No hidden markups

## USD Payouts

**ACH (Automated Clearing House)**
- For US bank accounts
- 2-3 business days
- Lower fees

**Wire Transfer**
- Domestic and international
- 1-2 business days
- Higher fees, faster

## EUR Payouts

**SEPA (Single Euro Payments Area)**
- For European accounts
- 1 business day
- Low fees

**International Wire**
- Non-SEPA countries
- 2-3 business days
- Standard wire fees

## MXN Payouts

**SPEI (Sistema de Pagos Electrónicos Interbancarios)**
- For Mexican accounts
- Same day (business hours)
- Very low fees
    `.trim(),
  },
  {
    id: "wd-004",
    slug: "wire-vs-ach-vs-sepa",
    title: "Wire vs ACH vs SEPA",
    description: "Understanding different payment rails",
    category: "withdrawals",
    order: 4,
    keywords: ["wire", "ach", "sepa", "transfer", "payment"],
    content: `
# Wire vs ACH vs SEPA

Understanding the different ways to receive your payouts.

## Quick Comparison

| Feature | ACH | Wire | SEPA |
|---------|-----|------|------|
| Region | USA | Global | Europe |
| Speed | 2-3 days | 1-2 days | 1 day |
| Fee | $0 | $15-25 | €0-3 |
| Limit | $25,000 | $100,000+ | €100,000 |
| Best For | Regular payouts | Large/urgent | European accounts |

## ACH (Automated Clearing House)

**What it is:**
US domestic payment network

**Pros:**
- Free or very low cost
- Reliable and established
- Good for recurring payments

**Cons:**
- Takes 2-3 business days
- US accounts only
- Lower per-transaction limits

**Best for:**
- Regular, non-urgent payouts
- Cost-conscious users
- US bank accounts

## Wire Transfer

**What it is:**
Bank-to-bank electronic transfer

**Pros:**
- Fastest option
- Higher limits
- International capable

**Cons:**
- Higher fees ($15-25)
- Receiving bank may charge too
- Overkill for small amounts

**Best for:**
- Large payouts
- Time-sensitive transfers
- International accounts

## SEPA (Single Euro Payments Area)

**What it is:**
European payment integration

**Pros:**
- Fast (often same day)
- Low fees (€0-3)
- 36 European countries

**Cons:**
- EUR only
- Europe only
- Some limits apply

**Best for:**
- European users
- EUR payouts
- Regular transfers

## Choosing the Right Method

### For USA Users
- < $5,000: ACH
- > $5,000 or urgent: Wire

### For European Users
- SEPA for everything EUR
- Wire for other currencies

### For Mexico Users
- SPEI for all MXN payouts
    `.trim(),
  },
  {
    id: "wd-005",
    slug: "payout-processing-times",
    title: "Payout Processing Times",
    description: "How long payouts take to reach your bank",
    category: "withdrawals",
    order: 5,
    keywords: ["processing", "time", "duration", "waiting", "speed"],
    content: `
# Payout Processing Times

Understand when to expect funds in your bank account.

## Processing Stages

### 1. Initiation (Instant)
- Payout request submitted
- Compliance checks performed
- Status: "Processing"

### 2. Bank Submission (0-4 hours)
- Sent to payment processor
- Bank receives instruction
- Status: "Submitted"

### 3. Bank Processing (Varies)
- Bank processes transfer
- Depends on payment method
- Status: "In Transit"

### 4. Settlement (Complete)
- Funds in your account
- Status: "Completed"

## Expected Timelines

| Method | Supabank Processing | Bank Processing | Total |
|--------|--------------------:|----------------:|------:|
| ACH | Same day | 2-3 days | 2-3 days |
| Wire | Same day | 1-2 days | 1-2 days |
| SEPA | Same day | 1 day | 1 day |
| SPEI | Same day | Same day | Same day |

## Business Days

- Processing happens Mon-Fri
- Excludes bank holidays
- Cut-off times apply
- Weekend requests queue for Monday

## Cut-off Times

| Method | Cut-off (ET) | Same-day Processing |
|--------|--------------|---------------------|
| ACH | 4:00 PM | If before cut-off |
| Wire | 4:00 PM | If before cut-off |
| SEPA | 2:00 PM CET | If before cut-off |
| SPEI | 5:00 PM CT | If before cut-off |

## Delays

Payouts may be delayed for:
- First-time large amounts
- New bank accounts
- Additional verification needed
- Bank holidays

## Tracking Your Payout

1. Go to Transactions
2. Find payout transaction
3. View detailed status
4. See estimated arrival
    `.trim(),
  },
  {
    id: "wd-006",
    slug: "failed-payouts-troubleshooting",
    title: "Failed Payouts Troubleshooting",
    description: "What to do when a payout fails",
    category: "withdrawals",
    order: 6,
    keywords: ["failed", "error", "rejected", "returned", "troubleshooting"],
    content: `
# Failed Payouts Troubleshooting

Sometimes payouts don't complete successfully. Here's how to resolve issues.

## Common Failure Reasons

### 1. Incorrect Bank Details
- Wrong account number
- Invalid routing number
- Typos in IBAN

**Solution:** Verify and re-enter bank details

### 2. Account Name Mismatch
- Name doesn't match your Supabank profile
- Joint account issues
- Business vs personal account

**Solution:** Use account in your verified name

### 3. Bank Rejection
- Account closed
- Account frozen
- Bank doesn't accept transfers

**Solution:** Contact your bank or use different account

### 4. Limit Exceeded
- Daily/monthly limit reached
- Single transaction too large

**Solution:** Wait for limit reset or split payout

## What Happens to Failed Payouts

1. **Return Notice** - We're notified of rejection
2. **Funds Credited Back** - Money returns to your balance
3. **Notification** - You receive email notification
4. **Ready for Retry** - Can attempt again

## Timeline for Returns

| Method | Return Time |
|--------|-------------|
| ACH | 2-3 business days |
| Wire | 1-2 business days |
| SEPA | 1 business day |

## Resolving Issues

### Step 1: Check Details
1. Go to Settings > Payouts
2. Review bank account details
3. Compare with bank statement
4. Fix any errors

### Step 2: Contact Bank
- Verify account can receive transfers
- Check for any restrictions
- Confirm account is active

### Step 3: Retry Payout
1. Ensure details are correct
2. Initiate new payout
3. Monitor status

### Step 4: Contact Support
If issues persist:
- Email support@supabank.xyz
- Include: transaction ID, error message
- We'll investigate with payment processor
    `.trim(),
  },
  {
    id: "wd-007",
    slug: "payout-fees-explained",
    title: "Payout Fees Explained",
    description: "Understanding withdrawal fees",
    category: "withdrawals",
    order: 7,
    keywords: ["fees", "cost", "charges", "pricing", "withdrawal"],
    content: `
# Payout Fees Explained

Transparent breakdown of all payout-related fees.

## Fee Structure

### Supabank Fees

| Method | Our Fee |
|--------|---------|
| ACH | $0 |
| Domestic Wire | $15 |
| International Wire | $25 |
| SEPA | €0 |
| SPEI | $0 |

### Conversion Fees

When crypto is converted to fiat:
- **Platform fee:** 2.0% of conversion amount
- **Bridge fee:** ~0.1% of conversion amount
- **Total:** ~2.1% of conversion amount

All fees are displayed before you confirm any transaction.

## Third-Party Fees

### Your Bank May Charge

| Fee Type | Amount |
|----------|--------|
| Incoming wire fee | $10-25 |
| International transfer fee | $15-50 |
| Currency conversion | 1-3% |

*Check with your bank for their specific fees*

### Intermediary Banks

For international wires:
- May deduct fees en route
- $10-30 typical
- SWIFT network charges

## Fee Examples

### Example 1: US ACH
- Payout amount: $1,000
- Supabank fee: $0
- Bank fee: $0 (typically)
- **You receive: $1,000**

### Example 2: International Wire
- Payout amount: $10,000
- Supabank fee: $25
- Intermediary: ~$20
- Your bank: ~$15
- **You receive: ~$9,940**

### Example 3: SEPA
- Payout amount: €5,000
- Supabank fee: €0
- Your bank: €0-3
- **You receive: €4,997-5,000**

## Minimizing Fees

**Best practices:**
- Use ACH for US (free)
- Use SEPA for Europe (free)
- Consolidate small payouts
- Choose banks with low incoming fees
    `.trim(),
  },
  {
    id: "wd-008",
    slug: "withdrawal-limits",
    title: "Withdrawal Limits",
    description: "Understanding your payout limits",
    category: "withdrawals",
    order: 8,
    keywords: ["limits", "maximum", "daily", "monthly", "restriction"],
    content: `
# Withdrawal Limits

Your payout limits depend on your verification level and account history.

## Limit Tiers

### Standard (Default)
- Per transaction: $10,000
- Daily: $25,000
- Monthly: $100,000

### Enhanced
- Per transaction: $50,000
- Daily: $100,000
- Monthly: $500,000

### Enterprise
- Per transaction: Custom
- Daily: Custom
- Monthly: Custom

## How Limits Are Calculated

### Daily Limits
- Reset at midnight UTC
- Includes all payouts
- Across all bank accounts

### Monthly Limits
- Calendar month
- Reset on 1st of each month
- Cumulative across methods

## Checking Your Limits

1. Go to Settings > Limits
2. View current tier
3. See remaining limits
4. Track usage

## Increasing Limits

### Standard → Enhanced
Requirements:
- 30 days account age
- Successful transactions
- Additional verification:
  - Proof of address
  - Source of funds

### Enhanced → Enterprise
Requirements:
- Business account
- High volume history
- Dedicated review
- Contact sales team

## Limit Holds

Temporary limit reductions may occur for:
- New accounts
- Unusual activity patterns
- Pending verification
- Security concerns

## Tips

**Best practices:**
- Plan large withdrawals ahead
- Verify before limit resets
- Contact support for increases
- Use multiple smaller transactions if needed
    `.trim(),
  },
];
